import { type AxiosRequestConfig } from 'axios';

import { axiosInstance } from './axios';

import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME } from './const';
import { getCookie, setCookie } from './cookie';

/**
 * NOTICE: 현재 파일에서 사용하는 axios instance는 src/lib/axios/client 입니다.
 *
 * 기존에 사용하던 axios instance는 default값을 수정하면 전역적으로 변경이 되어서
 * headers 값이 필요하지 않은 axios 요청에도 header값이 들어가는 이슈가 발생하여(다운로드, 이미지 업로드)
 * default axios instance 대신 새로 생성한 axiosInstance(src/lib/axios/client)를 사용하여
 * axiosInstance를 사용한 요청에 대해서만 토큰 재발급, 재요청이 실행되도록 수정하였습니다.
 */

// CustomAxiosRequestConfig 타입 정의
export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: string | PromiseLike<string> | null) => void;
  reject: (reason?: any) => void;
}> = [];

/**
 * 토큰 재발급 함수
 */
const reissuanceToken = async (): Promise<string> => {
  try {
    const refreshToken = getCookie(REFRESH_TOKEN_NAME);

    const {
      data: { accessToken },
    } = await axiosInstance({
      method: 'post',
      baseURL: process.env.NEXT_PUBLIC_SERVER_API_URL,
      url: '/v1/user-actions/token/renew',
      headers: {
        refreshToken,
      },
    });

    return accessToken;
  } catch (error: any) {
    console.log('ERROR : reissuanceToken', error);
    return Promise.reject(error);
  }
};

/**
 * Queue에 쌓인 요청들을 처리하는 함수
 */
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const addToQueue = (request: CustomAxiosRequestConfig) => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  })
    .then((token: unknown) => {
      if (typeof token === 'string') {
        request.headers = request.headers || {};
        request.headers['Authorization'] = 'Bearer ' + token;
        return axiosInstance(request);
      }
      throw new Error('Token must be a string');
    })
    .catch((err: any) => {
      return Promise.reject(err);
    });
};

/**
 * 기존 요청을 토큰을 발급 받고 재요청 하는 함수
 */
const retryOriginalRequest = (originalRequest: CustomAxiosRequestConfig) => {
  return reissuanceToken()
    .then((token: string) => {
      const isLocalhost = process.env.NODE_ENV === 'development';
      const domain = isLocalhost ? 'localhost' : '.cccv.to';
      setCookie(ACCESS_TOKEN_NAME, token, { path: '/', domain });
      axiosInstance.defaults.headers.common['Authorization'] =
        'Bearer ' + token;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers['Authorization'] = 'Bearer ' + token;
      processQueue(null, token);
      return axiosInstance(originalRequest);
    })
    .catch((err: any) => {
      processQueue(err, null);
      return Promise.reject(err);
    })
    .finally(() => {
      isRefreshing = false;
    });
};

const reissuanceTokenHanlder = (originalRequest: CustomAxiosRequestConfig) => {
  if (isRefreshing) {
    return addToQueue(originalRequest);
  }

  isRefreshing = true;
  return retryOriginalRequest(originalRequest);
};

export default reissuanceTokenHanlder;
